import { Box, Button, Typography, useTheme } from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'

import CreateQuizDialog from '../../../components/exams-components/quiz-questionbank/CreateQuizDialog'
import QuizMonthlyReportDialog from '../../../components/exams-components/quiz-questionbank/QuizMonthlyReportDialog'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import CustomTable from '../../../components/common/custom-table/CustomTable'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import { useSubjectStore } from '../../../store/subject.store'
import { Icons, formatDate } from '../../../utils/utilities'
import CountCard from '../../../components/common/CountCard'
import { useUserStore } from '../../../store/user.store'
import noQuizImage from '../../../assets/no-quiz.svg'
import { toast } from 'react-toastify'
import api from '../../../service/api'

const columns = [
    { field: "title", name: "Title", align: "left", minWidth: "180px", rowPadding: "10px 20px" },
    { field: "created_at", name: "Created On", renderCell: quiz => formatDate( quiz.created_at ), minWidth: "150px", align: "left" },
    { field: "subject", name: "Subject", renderCell: quiz => quiz.subject.subject_name, align: "left", minWidth: "200px" },
    { field: "topic_count", name: "Topic Count", renderCell: quiz => quiz?.question_bank_topics?.length || 0, minWidth: "150px", align: "center" },
    { field: "number_of_question", name: "Question Count", minWidth: "170px", align: "center" },
]


const Quizes = observer( ( { fromComponent = 'subject' } ) => {

    const [loading, setLoading] = useState( true )
    const [createDialogState, setCreateDialogState] = useState( false )
    const [monthlyReportDialogState, setMonthlyReportDialogState] = useState( false )

    const QuizStore = useQuizAndQuestionbankStore()
    const SubjectStore = useSubjectStore()
    const UserStore = useUserStore()

    const { palette, layout } = useTheme()
    const { subject_code } = useParams()

    const navigate = useNavigate()


    const retrieveOldQuizzes = async () => {
        try {
            await api.quiz.retrieveLegacyAliveQuizzes()
            setLoading( true )
            await UserStore.fetchUser()
            await QuizStore.fetchQuizes()
            setLoading( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while retriving legacy quizzes!" )
        }
    }

    useEffect( () => {
        const getData = async () => {
            if ( QuizStore.getErrors.quizzes === true )
                await QuizStore.fetchQuizes()
            setLoading( false )
        }
        getData()
    }, [QuizStore, SubjectStore] )

    return (
        <Box padding={layout.pagePadding} flexGrow={1} display="flex" flexDirection="column" overflow="auto">
            {createDialogState && <CreateQuizDialog fromComponent={fromComponent} subject={SubjectStore?.getSubject} state={createDialogState} setState={setCreateDialogState} />}
            {monthlyReportDialogState && <QuizMonthlyReportDialog state={monthlyReportDialogState} setState={setMonthlyReportDialogState} />}
            <Box sx={{ display: "flex", flexGrow: 1, flexDirection: "column", overflow: "hidden", minHeight: "450px" }} elevation={0}>
                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap="10px" >
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Your Quizzes</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Manage your quizzes here.</Typography>
                    </Box>
                    <Box display="flex" gap="10px" flexWrap="qrap" alignItems="center">
                        <Button onClick={() => setMonthlyReportDialogState( true )} disableElevation startIcon={Icons.default.AnalyticsIcon} sx={{ textTransform: "capitalize" }} color="secondary">
                            View Monthly Report
                        </Button>
                        <Button onClick={() => setCreateDialogState( true )} variant="contained" disableElevation startIcon={Icons.default.AddIcon} sx={{ textTransform: "capitalize" }} color="primary">
                            Create Quiz
                        </Button>
                        {!UserStore.getUser?.data_sync_status?.quizzes && <Button onClick={retrieveOldQuizzes} variant="contained" disableElevation startIcon={Icons.default.Replay} sx={{ textTransform: "capitalize" }} color="secondary">
                            Retrieve Old Quizzes
                        </Button>}
                    </Box>
                </Box>

                {<Box marginTop="20px" display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                    <CountCard loading={loading} label='Total Quizzes' icon={Icons.default.QuizIcon} value={QuizStore.getQuizes.length} />
                    <CountCard loading={loading} label='Questions Quizzed' icon={Icons.default.QuestionMark} value={QuizStore.getQuizes.reduce( ( acc, quiz ) => acc += quiz.number_of_question, 0 )} />
                </Box  >}

                {!loading && QuizStore.getQuizes.length > 0 && <CustomTable tableKey="fquizzes" onRowClick={quiz => navigate( `${quiz.id}` )} containerStyles={{ marginTop: "10px" }} columns={columns} data={QuizStore.getQuizes} />}
                {!loading && QuizStore.getQuizes.length === 0 && <Box bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" justifyContent="center" gap="20px">
                    <img src={noQuizImage} width="150px" alt="No quizes" />
                    <Typography variant="subtitle2">You haven't created any quizes {subject_code ? <span>for <strong><em><u>{SubjectStore?.getSubject?.subject_name?.trim()}</u></em>!</strong></span> : "!"}</Typography>
                    {( fromComponent !== 'subject' || ( subject_code && !SubjectStore.getSubject?.legacy_subject ) ) && <Button onClick={() => setCreateDialogState( true )} startIcon={Icons.default.AddIcon} variant='outlined' color="greyed"  >Create one</Button>}
                </Box>}
                {loading && <DataLoadingSpinner padding='20px 0' waitingMessage="Loading quizes..." />}
            </Box>
        </Box>
    )
} )

export default Quizes
